/* eslint-disable max-lines */
import {createStylesParams, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';
import {StyleType, wixColor, wixFont, CssColor, CssFont} from '@wix/editor-platform-ooi-metadata-resolver';
import type {CustomCssVarsFn} from '@wix/yoshi-flow-editor';
import {StyleParam} from '../../common/constants';
import {toDisplayProperty} from '../../domain/utils/styleParams.utils';

type IStylesParams = {
  [StyleParam.cartTitleVisibility]: StyleParamType.Boolean;
  [StyleParam.cartNumberOfCartItemsVisibility]: StyleParamType.Boolean;
  [StyleParam.CartItemQuantityCounterVisibility]: StyleParamType.Boolean;
  [StyleParam.CartItemImageVisibility]: StyleParamType.Boolean;
  [StyleParam.CartItemInfo]: StyleParamType.Boolean;
  [StyleParam.CustomerInputsPromoCodeVisibility]: StyleParamType.Boolean;
  [StyleParam.CustomerInputsNoteVisibility]: StyleParamType.Boolean;
  [StyleParam.CartSummaryDisclaimerVisibility]: StyleParamType.Boolean;
  [StyleParam.CartSummarySecureBadgeVisibility]: StyleParamType.Boolean;
  [StyleParam.ShowCheckoutButton]: StyleParamType.Boolean;
  [StyleParam.ShowGoToCartButton]: StyleParamType.Boolean;
  [StyleParam.ShowExpressCheckout]: StyleParamType.Boolean;
  [StyleParam.CartTextFontStyle]: StyleParamType.Font;
  [StyleParam.CartItemsBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartItemsDividerColor]: StyleParamType.Color;
  [StyleParam.CartItemsDividerWidth]: StyleParamType.Number;
  [StyleParam.CartItemsDividerXPadding]: StyleParamType.Number;
  [StyleParam.CartItemsDeleteItemButtonColor]: StyleParamType.Color;
  [StyleParam.CartItemNameFont]: StyleParamType.Font;
  [StyleParam.CartItemNameColor]: StyleParamType.Color;
  [StyleParam.CartItemDiscountNameFont]: StyleParamType.Font;
  [StyleParam.CartItemDiscountNameColor]: StyleParamType.Color;
  [StyleParam.CartItemMoreDetailsFont]: StyleParamType.Font;
  [StyleParam.CartItemMoreDetailsColor]: StyleParamType.Color;
  [StyleParam.CartHeaderBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartHeaderDividerColor]: StyleParamType.Color;
  [StyleParam.CartHeaderDividerWidth]: StyleParamType.Number;
  [StyleParam.CartHeaderDividerPadding]: StyleParamType.Number;
  [StyleParam.CartTitleFont]: StyleParamType.Font;
  [StyleParam.CartTitleColor]: StyleParamType.Color;
  [StyleParam.CartSubtitleFont]: StyleParamType.Font;
  [StyleParam.CartSubtitleColor]: StyleParamType.Color;
  [StyleParam.CartCloseButtonColor]: StyleParamType.Color;
  [StyleParam.CartCloseButtonSize]: StyleParamType.Number;
  [StyleParam.CartItemPriceFont]: StyleParamType.Font;
  [StyleParam.CartItemPriceColor]: StyleParamType.Color;
  [StyleParam.CartItemStrikethroughPriceColor]: StyleParamType.Color;
  [StyleParam.CartItemTotalPriceFont]: StyleParamType.Font;
  [StyleParam.CartItemTotalPriceColor]: StyleParamType.Color;
  [StyleParam.CartItemQuantityBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartItemQuantityBorderColor]: StyleParamType.Color;
  [StyleParam.CartItemQuantityBorderWidth]: StyleParamType.Number;
  [StyleParam.CartItemQuantityBorderRadius]: StyleParamType.Number;
  [StyleParam.CartItemQuantityFont]: StyleParamType.Font;
  [StyleParam.CartItemQuantityFontColor]: StyleParamType.Color;
  [StyleParam.CustomerInputsBackgroundColor]: StyleParamType.Color;
  [StyleParam.CustomerInputsLinkTextFont]: StyleParamType.Font;
  [StyleParam.CustomerInputsLinkTextFontColor]: StyleParamType.Color;
  [StyleParam.CustomerInputsDividerColor]: StyleParamType.Color;
  [StyleParam.CustomerInputsDividerWidth]: StyleParamType.Number;
  [StyleParam.CustomerInputsDividerXPadding]: StyleParamType.Number;
  [StyleParam.CartSummaryBoxBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartSummaryBoxDividerColor]: StyleParamType.Color;
  [StyleParam.CartSummaryBoxDividerWidth]: StyleParamType.Number;
  [StyleParam.CartSummaryBoxDividerXPadding]: StyleParamType.Number;
  [StyleParam.CartSummaryBoxSubtotalColor]: StyleParamType.Color;
  [StyleParam.CartSummaryBoxSubtotalFont]: StyleParamType.Font;
  [StyleParam.CartSummaryBoxDisclaimerColor]: StyleParamType.Color;
  [StyleParam.CartSummaryBoxDisclaimerFont]: StyleParamType.Font;
  [StyleParam.CartSummaryBoxBadgeColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonBackgroundColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonBorderColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonBorderWidth]: StyleParamType.Number;
  [StyleParam.GoToCartButtonCornerRadius]: StyleParamType.Number;
  [StyleParam.GoToCartButtonFont]: StyleParamType.Font;
  [StyleParam.GoToCartButtonFontColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonBackgroundHoverColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonBorderHoverColor]: StyleParamType.Color;
  [StyleParam.GoToCartButtonTextHoverColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonBackgroundColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonBorderColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonBorderWidth]: StyleParamType.Number;
  [StyleParam.GoToCheckoutButtonCornerRadius]: StyleParamType.Number;
  [StyleParam.GoToCheckoutButtonFont]: StyleParamType.Font;
  [StyleParam.GoToCheckoutButtonFontColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonBackgroundHoverColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonBorderHoverColor]: StyleParamType.Color;
  [StyleParam.GoToCheckoutButtonTextHoverColor]: StyleParamType.Color;
  [StyleParam.CartTextColor]: StyleParamType.Color;
  [StyleParam.CartDiscountNameColor]: StyleParamType.Color;
  [StyleParam.CartTitlesColor]: StyleParamType.Color;
};

const WstColors = {
  systemLineColor: 'var(--wst-system-line-1-color)',
  linksAndActionsColor: 'var(--wst-links-and-actions-color)',
};

export default createStylesParams<IStylesParams>({
  [StyleParam.cartTitleVisibility]: {
    key: 'CART_TITLE_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.cartNumberOfCartItemsVisibility]: {
    key: 'CART_NUMBER_OF_CART_ITEMS_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CartItemQuantityCounterVisibility]: {
    key: 'CART_ITEM_QUANTITY_COUNTER_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CartItemImageVisibility]: {
    key: 'CART_ITEM_IMAGE_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CartItemInfo]: {
    key: 'CART_ITEM_INFO',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CustomerInputsPromoCodeVisibility]: {
    key: 'CUSTOMER_INPUTS_PROMO_CODE_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CustomerInputsNoteVisibility]: {
    key: 'CUSTOMER_INPUTS_NOTE_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  [StyleParam.CartSummaryDisclaimerVisibility]: {
    key: 'CART_SUMMARY_DISCLAIMER_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.CartSummarySecureBadgeVisibility]: {
    key: 'CART_SUMMARY_SECURE_BADGE_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowCheckoutButton]: {
    key: 'CART_ACTION_BUTTONS_CHECKOUT_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  [StyleParam.ShowGoToCartButton]: {
    key: 'CART_ACTION_BUTTONS_GO_TO_CART_VISIBILITY',
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  [StyleParam.ShowExpressCheckout]: {
    key: 'cart_showExpressCheckout',
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },
  [StyleParam.CartTextFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'Base font used for cart item names, prices, quantities, subtotal, and input fields.',
      },
    },
  },
  [StyleParam.CartItemsBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CartItemsDividerColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.systemLineColor,
        opacity: 0.3,
      },
    },
  },
  [StyleParam.CartItemsDividerWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.CartItemsDividerXPadding]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(20),
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  [StyleParam.CartItemsDeleteItemButtonColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartItemNameFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.CartItemNameColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartItemDiscountNameFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph3Font),
    },
  },
  [StyleParam.CartItemDiscountNameColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StyleParam.CartItemMoreDetailsFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph3Font),
    },
  },
  [StyleParam.CartItemMoreDetailsColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartHeaderBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CartHeaderDividerColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  },
  [StyleParam.CartHeaderDividerWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.CartHeaderDividerPadding]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(20),
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  [StyleParam.CartTitleFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading2Font),
    },
  },
  [StyleParam.CartTitleColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstHeading2Color),
    },
  },
  [StyleParam.CartSubtitleFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.CartSubtitleColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartCloseButtonColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartCloseButtonSize]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(24),
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
  },
  [StyleParam.CartItemPriceFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph3Font),
    },
  },
  [StyleParam.CartItemPriceColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartItemStrikethroughPriceColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph3Color),
    },
  },
  [StyleParam.CartItemTotalPriceFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.CartItemTotalPriceColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CartItemQuantityBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CartItemQuantityBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  },
  [StyleParam.CartItemQuantityBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.CartItemQuantityBorderRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StyleParam.CartItemQuantityFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.CartItemQuantityFontColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StyleParam.CustomerInputsBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CustomerInputsLinkTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'Font of the text buttons for adding promo code and buyer note.',
      },
    },
  },
  [StyleParam.CustomerInputsLinkTextFontColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: {
        description: 'Color of the text buttons for adding promo code and buyer note.',
      },
    },
  },
  [StyleParam.CustomerInputsDividerColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.systemLineColor,
        opacity: 0.3,
      },
    },
  },
  [StyleParam.CustomerInputsDividerWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.CustomerInputsDividerXPadding]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(20),
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  [StyleParam.CartSummaryBoxBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CartSummaryBoxDividerColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.systemLineColor,
        opacity: 0.3,
      },
    },
  },
  [StyleParam.CartSummaryBoxDividerWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.CartSummaryBoxDividerXPadding]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(20),
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  [StyleParam.CartSummaryBoxSubtotalFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading2Font),
      ai: {
        description: 'Font of the subtotal or total amount displayed in the summary box.',
      },
    },
  },
  [StyleParam.CartSummaryBoxSubtotalColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'Color of the subtotal or total amount displayed in the summary box.',
      },
    },
  },
  [StyleParam.CartSummaryBoxDisclaimerFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph3Font),
      ai: {
        description: 'Font of disclaimer text (such as currency converter or tax disclaimers) in the summary box.',
      },
    },
  },
  [StyleParam.CartSummaryBoxDisclaimerColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph3Color),
      ai: {
        description: 'Color of disclaimer text (such as currency converter or tax disclaimers) in the summary box.',
      },
    },
  },
  [StyleParam.CartSummaryBoxBadgeColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'Color of the secure checkout badge in the summary box.',
      },
    },
  },
  [StyleParam.GoToCartButtonBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'var(--wst-primary-background-color)',
        opacity: 0,
      },
    },
  },
  [StyleParam.GoToCartButtonBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StyleParam.GoToCartButtonBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StyleParam.GoToCartButtonCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StyleParam.GoToCartButtonFontColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StyleParam.GoToCartButtonFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.GoToCartButtonBackgroundHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'var(--wst-primary-background-color)',
        opacity: 0,
      },
    },
  },
  [StyleParam.GoToCartButtonBorderHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.linksAndActionsColor,
        opacity: 0.7,
      },
    },
  },
  [StyleParam.GoToCartButtonTextHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: WstColors.linksAndActionsColor,
        opacity: 0.7,
      },
    },
  },
  [StyleParam.GoToCheckoutButtonBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StyleParam.GoToCheckoutButtonBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StyleParam.GoToCheckoutButtonBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(0),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StyleParam.GoToCheckoutButtonCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StyleParam.GoToCheckoutButtonFontColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.GoToCheckoutButtonFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StyleParam.GoToCheckoutButtonBackgroundHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.linksAndActionsColor,
        opacity: 0.7,
      },
    },
  },
  [StyleParam.GoToCheckoutButtonBorderHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: WstColors.linksAndActionsColor,
        opacity: 0,
      },
    },
  },
  [StyleParam.GoToCheckoutButtonTextHoverColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StyleParam.CartTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'Base color used for cart item names, prices, quantities, subtotal, and input fields.',
      },
    },
  },
  [StyleParam.CartDiscountNameColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: {
        description: 'Color of discount names displayed on cart items.',
      },
    },
  },
  [StyleParam.CartTitlesColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstHeading2Color),
    },
  },
});

export const customCssVars: CustomCssVarsFn = ({styleParams: {booleans}}) => {
  return {
    shouldDisplayCheckoutButton: toDisplayProperty(booleans[StyleParam.ShowCheckoutButton]),
    shouldDisplayGoToCartButton: toDisplayProperty(booleans[StyleParam.ShowGoToCartButton]),
    expressCheckoutButtonDisplay: toDisplayProperty(booleans[StyleParam.ShowExpressCheckout]),
    cartItemQuantityCounterVisibility: toDisplayProperty(booleans[StyleParam.CartItemQuantityCounterVisibility]),
    cartItemImageVisibility: toDisplayProperty(booleans[StyleParam.CartItemImageVisibility]),
    cartItemInfoVisibility: toDisplayProperty(booleans[StyleParam.CartItemInfo]),
    customerInputsPromoCodeVisibility: toDisplayProperty(booleans[StyleParam.CustomerInputsPromoCodeVisibility]),
    customerInputsNoteVisibility: toDisplayProperty(booleans[StyleParam.CustomerInputsNoteVisibility]),
    cartSummaryDisclaimerVisibility: toDisplayProperty(booleans[StyleParam.CartSummaryDisclaimerVisibility]),
    cartSummarySecureBadgeVisibility: toDisplayProperty(booleans[StyleParam.CartSummarySecureBadgeVisibility], 'flex'),
    cartTitleVisibility: toDisplayProperty(booleans[StyleParam.cartTitleVisibility]),
    cartNumberOfCartItemsVisibility: toDisplayProperty(booleans[StyleParam.cartNumberOfCartItemsVisibility]),
  };
};
